import { toast } from 'react-toastify'
import React, { useCallback, useState } from 'react'
import { getSPDataWithParams, postSPDataWithParams, putSPDataWithParams } from 'src/supportPortal/services/services'
import { buildTitleForAPrescriptionTemplate } from 'src/mentor/components/MentorPrescriptionTemplates/components/utils/prescriptionCreateUtils'
import {
  buildPrescriptionTemplatePayload,
  normalizePrescriptionTemplateFromApi,
  getDefaultNewPrescriptionTemplatePayload,
  PRESCRIPTION_TEMPLATE_TYPE_PDF,
} from 'src/mentor/components/MentorPrescriptionTemplates/components/utils/prescriptionTemplatePayload'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

export default function useSupportPrescriptionTemplateApiActions({
  selectedTemplateType,
  paperPrescriptionTemplate,
  pdfPrescriptionTemplate,
  setPaperPrescriptionTemplate,
  setPdfPrescriptionTemplate,
  setPdfOptions,
  setPaperOptions,
  setConfirmationModalProps,
  clinicId,
  mentorSlug,
  associatedClinics,
  clinicBrands,
  mentorProfileData,
}) {
  const [loading, setLoading] = useState()
  const [saving, setSaving] = useState(false)

  const relevantTemplate =
    selectedTemplateType === 'print' ? paperPrescriptionTemplate : pdfPrescriptionTemplate
  const relevantSetTemplate =
    selectedTemplateType === 'print' ? setPaperPrescriptionTemplate : setPdfPrescriptionTemplate

  function setTemplateData(
    relevantTemplates,
    setTemplateOptions,
    setTemplateData,
    silentlyUpdateData = false,
  ) {
    const templates = relevantTemplates?.map((template, idx) => {
      const normalized = normalizePrescriptionTemplateFromApi(template)
      const clinicLocation = associatedClinics?.find(
        (location) => location?.id === template?.clinicId,
      )
      const clinicBrand = clinicBrands?.find((brand) => brand?.id === template?.clinicBrandId)
      const mentorProfile = mentorProfileData
      return {
        ...normalized,
        title: buildTitleForAPrescriptionTemplate({
          clinicLocationData: clinicLocation,
          clinicBrandData: clinicBrand,
          mentorProfileData: mentorProfile,
          idx: idx,
          prescriptionTemplate: normalized,
        }),
      }
    })
    const activeTemplate = templates?.find((t) => t?.active)
    setTemplateOptions(templates || [])
    if (silentlyUpdateData) {
      return
    }
    if (activeTemplate) {
      setTemplateData(activeTemplate)
    } else {
      setTemplateData(templates?.[0] || {})
    }
  }

  async function getAndSetPrescriptionTemplate(
    prescriptionTemplateId,
    enableSilentRefresh = false,
    languageCode,
  ) {
    if (!enableSilentRefresh) {
      setLoading(true)
    }
    const useSlug = mentorSlug && !mentorProfileData?.id
    if (!mentorProfileData?.id && !mentorSlug) {
      if (!enableSilentRefresh) {
        setLoading(false)
      }
      return
    }
    try {
      const params = {
        clinicId: clinicId === 'private' ? null : clinicId,
      }
      if (languageCode) params.languageCode = languageCode
      if (useSlug) {
        params['practitioner-slug'] = mentorSlug
      } else {
        params['practitioner-id'] = mentorProfileData.id
      }
      const response = await getSPDataWithParams(
        '/api/secure/cc/support/practitioner/prescription-template',
        params,
        undefined,
        undefined,
        true,
      )
      if (!enableSilentRefresh) {
        setLoading(false)
      }
      if (response?.status === 200) {
        let templates = response?.data || []
        // Handle different response structures
        if (response?.data?.data && Array.isArray(response.data.data)) {
          templates = response.data.data
        } else if (getDataMapFromCommonResponse(response)?.['prescription-template']) {
          templates = Array.isArray(getDataMapFromCommonResponse(response)?.['prescription-template'])
            ? getDataMapFromCommonResponse(response)?.['prescription-template']
            : [getDataMapFromCommonResponse(response)?.['prescription-template']]
        }
        const pdfTemplates =
          templates?.filter((template) => template?.prescriptionTemplateType === 'pdf') || []
        const paperTemplates =
          templates?.filter(
            (template) =>
              template?.prescriptionTemplateType === 'print' || !template?.prescriptionTemplateType,
          ) || []
        setTemplateData(pdfTemplates, setPdfOptions, setPdfPrescriptionTemplate, enableSilentRefresh)
        setTemplateData(
          paperTemplates,
          setPaperOptions,
          setPaperPrescriptionTemplate,
          enableSilentRefresh,
        )
      }
    } catch (error) {
      console.error('Error fetching prescription template:', error)
      if (!enableSilentRefresh) {
        setLoading(false)
      }
    }
  }

  async function savePrescriptionTemplate({ enableSilentRefresh = false }) {
    setSaving(true)
    try {
      const templateToSave = buildPrescriptionTemplatePayload({
        ...relevantTemplate,
        prescriptionTemplateType: selectedTemplateType,
      })
      const response = await savePrescriptionTemplateApiCall({
        prescriptionTemplate: templateToSave,
        clinicId,
        practitionerId: mentorProfileData?.id,
        practitionerSlug: mentorSlug,
      })
      const responseData = getDataMapFromCommonResponse(response)
      const saved = responseData?.['prescription-template']
      relevantSetTemplate(saved ? normalizePrescriptionTemplateFromApi(saved) : saved)
      getAndSetPrescriptionTemplate(undefined, enableSilentRefresh)
      toast.success('Saved Prescription Template')
    } catch (error) {
      toast.error('Failed to save prescription template')
      console.error('Failed to save prescription template:', error)
    } finally {
      if (!enableSilentRefresh) {
        setSaving(false)
      }
    }
  }

  async function activatePrescriptionTemplate() {
    setSaving(true)
    try {
      await activatePrescriptionTemplateApiCall({
        prescriptionTemplate: relevantTemplate,
        clinicId,
        practitionerId: mentorProfileData?.id,
      })
      getAndSetPrescriptionTemplate()
    } catch (error) {
      toast.error('Failed to activate prescription template')
      console.error('Failed to activate prescription template:', error)
    } finally {
      setSaving(false)
    }
  }

  async function createPrescriptionTemplate() {
    setSaving(true)
    try {
      const response = await createPrescriptionTemplateApiCall({
        clinicId,
        practitionerId: mentorProfileData?.id,
        practitionerSlug: mentorSlug,
        templateType: PRESCRIPTION_TEMPLATE_TYPE_PDF,
      })
      getAndSetPrescriptionTemplate()
      relevantSetTemplate(response?.data)
      toast.success('Prescription template created successfully')
      return response
    } catch (error) {
      toast.error('Failed to create prescription template')
      console.error('Failed to create prescription template:', error)
    } finally {
      setSaving(false)
    }
  }

  async function deletePrescriptionTemplate() {
    setSaving(true)
    let templateToSave = { ...relevantTemplate }
    try {
      await deletePrescriptionTemplateApiCall({
        templateId: templateToSave?.id,
        practitionerId: mentorProfileData?.id,
      })
      getAndSetPrescriptionTemplate(undefined, true)
      toast.success('Prescription template deleted successfully')
    } catch (error) {
      toast.error('Failed to delete prescription template')
      console.error('Failed to delete prescription template:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleSubmit = () =>
    setConfirmationModalProps({
      visibility: true,
      visibilitySetter: setConfirmationModalProps,
      action: 'save',
      body: 'Do you want to save this prescription pad?',
      buttonColor: 'success',
      onSubmitFunctions: [() => savePrescriptionTemplate({ enableSilentRefresh: true })],
    })

  function handleDelete() {
    setConfirmationModalProps({
      visibility: true,
      visibilitySetter: setConfirmationModalProps,
      action: 'delete',
      body: 'Are you sure you want to delete this prescription pad? This action cannot be undone.',
      buttonColor: 'danger',
      onSubmitFunctions: [deletePrescriptionTemplate],
    })
  }

  return {
    getAndSetPrescriptionTemplate,
    savePrescriptionTemplate,
    addTemplate: createPrescriptionTemplate,
    deleteTemplate: handleDelete,
    activateTemplate: activatePrescriptionTemplate,
    loading,
    handleSubmit,
  }
}

export async function savePrescriptionTemplateApiCall({ prescriptionTemplate, clinicId, practitionerId, practitionerSlug }) {
  if (!practitionerId && !practitionerSlug) {
    return Promise.reject(new Error('practitionerId or practitionerSlug is required'))
  }
  const payload = buildPrescriptionTemplatePayload(prescriptionTemplate)
  const queryParams = practitionerSlug && !practitionerId
    ? { 'practitioner-slug': practitionerSlug }
    : { 'practitioner-id': practitionerId }
  const response = await postSPDataWithParams(
    '/api/secure/cc/support/practitioner/prescription-template/save',
    {
      ...payload,
      apiParam: 'prescription-template',
      clinicId: clinicId === 'private' ? null : clinicId,
    },
    queryParams,
  )
  if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return Promise.resolve(response)
  }
  return Promise.reject(response)
}

async function activatePrescriptionTemplateApiCall({ prescriptionTemplate, clinicId, practitionerId }) {
  if (!practitionerId) {
    return Promise.reject(new Error('practitionerId is required'))
  }
  const response = await putSPDataWithParams(
    '/api/secure/cc/support/practitioner/prescription-template/activate',
    {
      apiParam: 'prescription-template',
    },
    {
      'template-id': prescriptionTemplate.id,
      'practitioner-id': practitionerId,
    },
  )
  if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return Promise.resolve(response)
  }
  return Promise.reject(response)
}

async function createPrescriptionTemplateApiCall({ clinicId, practitionerId, practitionerSlug, templateType }) {
  if (!practitionerId && !practitionerSlug) {
    return Promise.reject(new Error('practitionerId or practitionerSlug is required'))
  }
  const payload = getDefaultNewPrescriptionTemplatePayload({
    prescriptionTemplateType: templateType || PRESCRIPTION_TEMPLATE_TYPE_PDF,
    clinicId: clinicId === 'private' ? null : clinicId,
  })
  const queryParams = practitionerSlug && !practitionerId
    ? { 'practitioner-slug': practitionerSlug }
    : { 'practitioner-id': practitionerId }
  const response = await postSPDataWithParams(
    '/api/secure/cc/support/practitioner/prescription-template/save',
    {
      ...payload,
      apiParam: 'prescription-template',
    },
    queryParams,
  )
  if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return Promise.resolve(response)
  }
  return Promise.reject(response)
}

async function deletePrescriptionTemplateApiCall({ templateId, practitionerId }) {
  if (!practitionerId) {
    return Promise.reject(new Error('practitionerId is required'))
  }
  const response = await putSPDataWithParams(
    `/api/secure/cc/support/practitioner/prescription-template/disable`,
    {
      apiParam: 'prescription-template',
    },
    {
      'template-id': templateId,
      'practitioner-id': practitionerId,
    },
  )
  if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return Promise.resolve(response)
  }
  return Promise.reject(response)
}

